import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildDeck } from '../constants/decks.js';
import DeckStop from '../deck/DeckStop.jsx';
import DeckBackground from '../deck/DeckBackground.jsx';
import { http } from '../api/http.js';
import { getTeacherToken } from '../hooks/useTeacherAuth.js';
import '../deck/deck.css';

const CHANNEL = 'lc-deck-preview';

const EMPTY_PULSE = { understand: { ok: 0, confused: 0 }, hands: [] };
const noop = () => Promise.resolve({ ok: false });

/**
 * A single slide, rendered for the control panel's miniatures. It is its own
 * route because deck.css sizes everything in `vw`/`vh` — 239 times over — so
 * a slide only looks right when it owns the whole viewport. Inside an iframe
 * it does, at whatever size the panel gives it; shrunk with `transform` in
 * the panel's own document it would not.
 *
 * The frame is loaded once and then told where to go over postMessage, so
 * walking the deck does not reload it or re-fetch the questions. It opens no
 * socket: everything live (tallies, hands, stars) belongs to the panel
 * around it, not to the picture of the slide.
 */
export default function DeckPreviewFrame() {
  const { roomId } = useParams();
  const [data, setData] = useState(null);
  const [{ index, substep }, setPos] = useState({ index: 0, substep: 0 });
  const readyRef = useRef(false);

  useEffect(() => {
    const token = getTeacherToken();
    if (!token) return;
    http
      .get(`/api/rooms/${roomId}`, { Authorization: `Bearer ${token}` })
      .then((res) => setData({ roomCode: res.room?.code || '', questions: res.questions || [] }))
      .catch(() => setData({ roomCode: '', questions: [] }));
  }, [roomId]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.channel !== CHANNEL) return;
      setPos({ index: Number(e.data.index) || 0, substep: Number(e.data.substep) || 0 });
    };
    window.addEventListener('message', onMessage);
    // The panel cannot know when this frame finished loading, so the frame
    // says so and the panel replies with the position it is already on.
    if (!readyRef.current && window.parent !== window) {
      readyRef.current = true;
      window.parent.postMessage({ channel: CHANNEL, ready: true }, window.location.origin);
    }
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const deck = useMemo(
    () => (data ? buildDeck(data.roomCode, data.questions) : null),
    [data]
  );

  const room = useMemo(
    () => ({
      roomCode: data?.roomCode || '',
      questions: data?.questions || [],
      question: null,
      leaderboard: [],
      players: [],
      connectedCount: 0,
      pulse: EMPTY_PULSE,
      reactions: [],
      status: 'lobby',
      busy: true, // nothing in a preview is meant to be clicked
      actions: {
        openQuestion: noop, reveal: noop, awardStar: noop,
        kickPlayer: noop, newSlide: noop, gotoSlide: noop, setOverlay: noop,
      },
    }),
    [data]
  );

  if (!deck) return <div className="deck-root is-preview" />;

  const stop = deck.stops[index] || deck.stops[0];
  if (!stop) return <div className="deck-root is-preview" />;

  return (
    <div className="deck-root is-preview" data-theme={deck.meta?.theme}>
      <DeckBackground kind={stop.kind} theme={deck.meta?.theme} />
      <div className="deck-stage" data-kind={stop.kind} data-part={stop.part || ''}>
        <DeckStop
          stop={stop}
          substep={substep}
          deck={deck}
          room={room}
          findQuestion={(order) => room.questions.find((q) => q.order_index === order) || null}
          onOpenQr={() => {}}
          preview
        />
      </div>
    </div>
  );
}

export { CHANNEL as PREVIEW_CHANNEL };
